import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MapPin, X } from "@phosphor-icons/react";

export default function ServiceAreaEditor({ tenant, onTenantChanged }) {
  const [areas, setAreas] = useState([]);
  const [areaZip, setAreaZip] = useState("");
  const [areaCity, setAreaCity] = useState("");
  const [zipsSaved, setZipsSaved] = useState(null);
  const [savingZips, setSavingZips] = useState(false);

  useEffect(() => {
    // Migration: wenn service_areas leer, aber service_area_zips vorhanden → als PLZ-Zeilen laden
    const existing = tenant?.service_areas;
    if (existing && existing.length > 0) {
      setAreas(existing.map((a) => ({ zip: a.zip || "", city: a.city || "" })));
    } else {
      setAreas((tenant?.service_area_zips || []).map((z) => ({ zip: z, city: "" })));
    }
  }, [tenant]);

  const addArea = () => {
    const z = (areaZip || "").replace(/\D/g, "").trim();
    const c = (areaCity || "").trim();
    if (!z && !c) return;
    if (areas.some((a) => a.zip === z && (a.city || "").toLowerCase() === c.toLowerCase())) {
      setAreaZip(""); setAreaCity(""); return;
    }
    setAreas([...areas, { zip: z, city: c }]);
    setAreaZip(""); setAreaCity("");
  };
  const removeArea = (idx) => setAreas(areas.filter((_, i) => i !== idx));
  const saveZips = async () => {
    setSavingZips(true);
    try {
      const cleaned = areas.filter((a) => (a.zip || a.city));
      const r = await api.patch(`/tenants/${tenant.tenant_id}/service-area`, { service_areas: cleaned });
      setZipsSaved({ ok: true, msg: "Gespeichert" });
      onTenantChanged?.(r.data);
      setTimeout(() => setZipsSaved(null), 2500);
    } catch (e) {
      setZipsSaved({ ok: false, msg: e.response?.data?.detail || "Fehler" });
    } finally { setSavingZips(false); }
  };

  return (
    <section className="bg-gradient-to-br from-sky-50 to-white border border-black/10 p-5" data-testid="service-area-panel">
      <div className="flex flex-wrap items-start gap-3 justify-between">
        <div className="flex-1 min-w-0">
          <h3 className="font-heading text-lg font-semibold tracking-tight flex items-center gap-2">
            <MapPin size={18} weight="bold" /> Servicegebiete
          </h3>
          <p className="text-xs text-zinc-500 mt-1 leading-relaxed">
            Diese Orte werden prominent auf Ihrer Landingpage angezeigt (&bdquo;Unsere Servicegebiete&ldquo;). Anfragen aus Adressen <strong>außerhalb</strong> dieser PLZs landen automatisch in der Interessenten-Liste.
            <em> Leer lassen = alle Gebiete werden bedient.</em>
          </p>
        </div>
      </div>

      {/* Add-Row */}
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-[140px_1fr_auto] gap-2 items-end">
        <div>
          <Label className="text-xs">PLZ</Label>
          <Input
            className="h-10 rounded-none font-mono text-sm"
            value={areaZip}
            onChange={(e) => setAreaZip(e.target.value.replace(/\D/g, "").slice(0, 5))}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addArea(); } }}
            placeholder="79576"
            data-testid="service-area-zip-input"
          />
        </div>
        <div>
          <Label className="text-xs">Ort / Stadt</Label>
          <Input
            className="h-10 rounded-none text-sm"
            value={areaCity}
            onChange={(e) => setAreaCity(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addArea(); } }}
            placeholder="Weil am Rhein"
            data-testid="service-area-city-input"
          />
        </div>
        <Button
          type="button"
          onClick={addArea}
          className="rounded-none bg-zinc-900 text-white hover:bg-black h-10"
          data-testid="add-service-area-btn"
        >
          Gebiet hinzufügen
        </Button>
      </div>

      {/* List */}
      {areas.length > 0 ? (
        <div className="mt-4 flex flex-wrap gap-2" data-testid="service-area-list">
          {areas.map((a, i) => (
            <div
              key={`${a.zip}-${a.city}-${i}`}
              className="inline-flex items-center gap-2 pl-3 pr-1 py-1 bg-white border border-black/10 rounded-full text-sm"
              data-testid={`service-area-item-${i}`}
            >
              {a.zip && <span className="font-mono text-xs bg-zinc-100 px-1.5 py-0.5 rounded">{a.zip}</span>}
              <span>{a.city || <span className="text-zinc-400 italic">nur PLZ</span>}</span>
              <button
                type="button"
                onClick={() => removeArea(i)}
                className="w-6 h-6 rounded-full hover:bg-red-100 text-red-600 flex items-center justify-center"
                aria-label="Entfernen"
                data-testid={`remove-service-area-${i}`}
              >
                <X size={12} weight="bold" />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="mt-4 text-xs text-zinc-400 italic">Noch keine Gebiete – bitte oben ergänzen (oder leer lassen für &bdquo;bundesweit&ldquo;).</div>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Button
          onClick={saveZips}
          disabled={savingZips}
          className="rounded-none bg-black text-white hover:bg-zinc-800 h-10 disabled:opacity-60"
          data-testid="save-service-area-btn"
        >
          {savingZips ? "Speichere…" : "Speichern"}
        </Button>
        {zipsSaved && <span className={`text-xs font-medium ${zipsSaved.ok ? "text-emerald-700" : "text-red-600"}`}>{zipsSaved.ok ? "✓" : "✗"} {zipsSaved.msg}</span>}
      </div>
    </section>

  );
}
